// contexts/FilterStateContext.js
import React, { createContext, useContext, useState } from 'react';
import { OP_DEFAULT } from 'constants/OpConstants';

// Create the context for filter state only (no data fetching)
const FilterStateContext = createContext();

// Provider component that only manages filter state
export function FilterStateProvider({ children, projectID }) {
  
  const [filters, setFilters] = useState({
    codes: [],
    multi_choices: {}, // Object with descriptor IDs as keys
    transcripts: [],
    surveys: [],
    op: OP_DEFAULT,
    sort: null
  });

  // Helper functions to update filters
  const updateFilters = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const toggleCode = (codeID) => {
    setFilters(prev => ({
      ...prev,
      codes: prev.codes.includes(codeID)
        ? prev.codes.filter(id => id !== codeID)
        : [...prev.codes, codeID]
    }));
  };

  const toggleTranscript = (transcriptID) => {
    setFilters(prev => ({
      ...prev,
      transcripts: prev.transcripts.includes(transcriptID)
        ? prev.transcripts.filter(id => id !== transcriptID)
        : [...prev.transcripts, transcriptID]
    }));
  };

  const toggleSurvey = (surveyID) => {
    setFilters(prev => ({
      ...prev,
      surveys: prev.surveys.includes(surveyID)
        ? prev.surveys.filter(id => id !== surveyID)
        : [...prev.surveys, surveyID]
    }));
  };

  const toggleDemographic = (descriptorID, choiceID) => {
    setFilters(prev => {
      // Create a new multi_choices object
      const newMultiChoices = { ...prev.multi_choices };
      
      // Initialize the array for this descriptor if it doesn't exist
      if (!newMultiChoices[descriptorID]) {
        newMultiChoices[descriptorID] = [];
      }
      
      // Check if the choice is already selected
      const choiceIndex = newMultiChoices[descriptorID].indexOf(choiceID);
      
      if (choiceIndex >= 0) {
        // Remove the choice if it's already selected
        newMultiChoices[descriptorID] = newMultiChoices[descriptorID].filter(id => id !== choiceID);
        
        // If no choices left for this descriptor, remove the descriptor key
        if (newMultiChoices[descriptorID].length === 0) {
          delete newMultiChoices[descriptorID];
        }
      } else {
        // Add the choice
        newMultiChoices[descriptorID] = [...newMultiChoices[descriptorID], choiceID];
      }
      
      return {
        ...prev,
        multi_choices: newMultiChoices
      };
    });
  };

  const clearCodes = () => {
    setFilters(prev => ({ ...prev, codes: [] }));
  };

  const clearTranscripts = () => {
    setFilters(prev => ({ ...prev, transcripts: [] }));
  };

  const clearDemographic = (descriptorID) => {
    setFilters(prev => {
      const newMultiChoices = { ...prev.multi_choices };
      
      // If descriptorID is provided, only clear that descriptor
      if (descriptorID) {
        delete newMultiChoices[descriptorID];
      } else {
        // Otherwise clear all demographics
        return { ...prev, multi_choices: {} };
      }
      
      return {
        ...prev,
        multi_choices: newMultiChoices
      };
    });
  };

  const clearSurveys = () => {
    setFilters(prev => ({ ...prev, surveys: [] }));
  };

  const setOperation = (op) => {
    setFilters(prev => ({ ...prev, op }));
  };

  const setSort = (sort) => {
    setFilters(prev => ({ ...prev, sort }));
  };

  const value = {
    // Filter state
    filters,
    
    // Actions
    toggleCode,
    toggleTranscript,
    toggleSurvey,
    toggleDemographic,
    clearCodes,
    clearTranscripts,
    clearSurveys,
    clearDemographic,
    setOperation,
    setSort,
    updateFilters,
    
    // Context metadata
    projectID
  };

  return (
    <FilterStateContext.Provider value={value}>
      {children}
    </FilterStateContext.Provider>
  );
}

// Custom hook to use the filter state context
export function useFilterState() {
  const context = useContext(FilterStateContext);
  if (context === undefined) {
    throw new Error('useFilterState must be used within a FilterStateProvider');
  }
  return context;
}

// Export the context for optional direct access
export default FilterStateContext;